<script setup lang="ts">
import { computed, ref } from 'vue';
import type { UploadCustomRequestOptions } from 'naive-ui';
import type { FlatResponseData } from '@sa/axios';
import _ from 'lodash';
import type { Api } from '@/typings/api';

const props = defineProps<{
  /** 模板名称 */
  templateName?: string;
  /** 上传方法 */
  importFn?: (file: File, data: any) => Promise<FlatResponseData>;
  /** 附加参数 */
  data: Api.Common.ImportParams;
  /** 标题显示 */
  title?: string;
}>();

interface Emits {
  (e: 'startUpload'): void;
  (e: 'success', data: any): void;
  (e: 'fail', msg: any): void;
  (e: 'finally'): void;
}

const emit = defineEmits<Emits>();
const show = ref<boolean>(false);
const hasErrMsg = ref<boolean>(false);
const msgTitle = ref<string>();
const msgContent = ref<string[]>([]);

// 下载模板
const templateUrl = computed(() => {
  if (props.templateName) {
    return `/template/${props.templateName}`;
  }
  return '#';
});

/** 上传 */
function handleUpload({ file }: UploadCustomRequestOptions) {
  if (props.importFn && props.importFn instanceof Function) {
    emit('startUpload');
    show.value = true;
    const data = _.cloneDeep(props.data);
    const extParams = data?.extParams;
    if (extParams && !_.isEmpty(extParams)) {
      data.extParams = typeof extParams === 'string' ? extParams : JSON.stringify(extParams);
    }
    props
      .importFn(file.file as File, data)
      .then(res => {
        if (res.data !== 'success') {
          const msg = res?.error?.response?.data?.msg;
          const code = res?.error?.response?.data?.code;
          if (msg && code !== 0) {
            hasErrMsg.value = true;
            if (!msg.includes('：')) {
              msgTitle.value = '导入失败';
              if (msg.includes(',')) {
                msgContent.value = msg.split(',');
              } else {
                msgContent.value = [msg];
              }
            } else {
              const msgArr = msg.split('：');
              if (msgArr && msgArr.length > 1) {
                msgTitle.value = msgArr[0];
                // eslint-disable-next-line max-depth
                if (msgTitle.value !== undefined) {
                  msgContent.value = msg.substring(msgTitle.value.length + 1).split(',');
                  // msgContent.value = msgArr[1].split(',');
                }
              }
            }
            emit('fail', msg);
          } else {
            hasErrMsg.value = false;
            try {
              emit('success', res.data);
            } catch (e) {
              console.log('🚀 ~ handleUpload ~ e:', e);
            }
          }
        } else {
          hasErrMsg.value = false;
          emit('success', res.data);
        }
      })
      .catch(e => {
        emit('fail', e);
      })
      .finally(() => {
        emit('finally');
        show.value = false;
      });
  }
}
</script>

<template>
  <div class="modal-upload">
    <NSpace vertical>
      <NSpin :show="show">
        <div v-if="props.title" class="mb-20px">
          当前数据导入至：
          <span class="font-bold">{{ props.title }}</span>
        </div>
        <text v-if="templateUrl && templateUrl != '#'" class="text-desc">
          请先下载模板：
          <a :href="templateUrl" download>模板</a>
        </text>
        <NUpload directory-dnd class="mt-[20px]" :custom-request="handleUpload">
          <NUploadDragger>
            <div style="margin-bottom: 12px">
              <icon-foundation-archive class="text-icon" />
            </div>
          </NUploadDragger>
        </NUpload>
        <div v-if="hasErrMsg" class="mt-[20px]">
          <div class="text-[#f5222d]">{{ msgTitle + '：' }}</div>
          <div v-for="item in msgContent" :key="item" class="text-[#f5222d]">
            {{ item }}
          </div>
        </div>
      </NSpin>
    </NSpace>
  </div>
</template>

<style lang="scss" scoped>
.modal-upload {
  .text-desc {
    margin-bottom: 20px;
    a {
      color: #3b82f6;
      cursor: pointer;
    }
  }
  .text-icon {
    font-size: 40px !important;
  }
}
</style>
